import { useEffect, useState, FormEvent } from 'react';
import { AppShell } from '../components/AppShell';
import { Modal } from '../components/Modal';
import { AddressInput } from '../components/AddressInput';
import { useConfirm } from '../components/ConfirmProvider';
import { useUserOrg } from '../lib/org';
import {
  listSubcontractors,
  updateSubcontractor,
  archiveSubcontractor,
  findOrCreateSubcontractor,
  type SubcontractorRow,
} from '../lib/db';
import { cacheListByOrg } from '../lib/cache';
import { BulkImportModal } from '../components/BulkImportModal';
import { LoadingIndicator } from '../components/LoadingIndicator';

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
}

interface Draft {
  company_name: string;
  trade: string;
  main_contact: string;
  mobile: string;
  address: string;
  pl_insurance_expiry: string;
  el_insurance_expiry: string;
  pi_insurance_expiry: string;
  first_aider: boolean;
  first_aid_cert_expiry: string;
}

export function RegisterSubbies() {
  const { org } = useUserOrg();
  const { confirm } = useConfirm();
  const [items, setItems] = useState<SubcontractorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<SubcontractorRow | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  async function refresh() {
    if (!org) return;
    setLoading(true);
    try {
      setItems(await listSubcontractors(org.org_id));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  // Initial load — stale-while-revalidate. Post-mutation refreshes
  // still call refresh() directly for network-only behaviour.
  useEffect(() => {
    if (!org) return;
    let cancelled = false;
    let haveCache = false;
    cacheListByOrg<SubcontractorRow>('subcontractors', org.org_id).then((cached) => {
      if (cancelled) return;
      if (cached.length > 0) {
        haveCache = true;
        setItems(cached);
        setLoading(false);
      }
    }).catch(() => {});
    listSubcontractors(org.org_id).then((list) => {
      if (cancelled) return;
      setItems(list);
      setError(null);
    }).catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled && !haveCache) setLoading(false); });
    return () => { cancelled = true; };
    /* eslint-disable-next-line */
  }, [org]);

  function startEdit(s: SubcontractorRow) {
    setEditing(s);
    setDraft({
      company_name: s.company_name,
      trade: s.trade ?? '',
      main_contact: s.main_contact ?? '',
      mobile: s.mobile ?? '',
      address: s.address ?? '',
      pl_insurance_expiry: s.pl_insurance_expiry ?? '',
      el_insurance_expiry: s.el_insurance_expiry ?? '',
      pi_insurance_expiry: s.pi_insurance_expiry ?? '',
      first_aider: s.first_aider,
      first_aid_cert_expiry: s.first_aid_cert_expiry ?? '',
    });
    setShowModal(true);
  }

  /**
   * Inline one-tap toggle for first-aider status. Used by the badge on each
   * row so you can bulk-promote known first aiders without opening the modal.
   * Optimistic update — the row flips immediately, with a rollback if the
   * server rejects (rare; mostly RLS).
   */
  async function toggleFirstAider(s: SubcontractorRow) {
    const next = !s.first_aider;
    setItems((prev) => prev.map((x) => (x.id === s.id ? { ...x, first_aider: next } : x)));
    try {
      await updateSubcontractor(s.id, { first_aider: next });
    } catch (e) {
      // Rollback
      setItems((prev) => prev.map((x) => (x.id === s.id ? { ...x, first_aider: !next } : x)));
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function onSave(e: FormEvent) {
    e.preventDefault();
    if (!editing || !draft) return;
    setSubmitting(true);
    try {
      await updateSubcontractor(editing.id, {
        company_name: draft.company_name.trim(),
        trade: draft.trade.trim() || null,
        main_contact: draft.main_contact.trim() || null,
        mobile: draft.mobile.trim() || null,
        address: draft.address.trim() || null,
        pl_insurance_expiry: draft.pl_insurance_expiry || null,
        el_insurance_expiry: draft.el_insurance_expiry || null,
        pi_insurance_expiry: draft.pi_insurance_expiry || null,
        first_aider: draft.first_aider,
        first_aid_cert_expiry: draft.first_aid_cert_expiry || null,
      });
      setShowModal(false);
      setEditing(null);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  async function onArchive(s: SubcontractorRow) {
    if (!(await confirm({
      title: `Archive ${s.company_name}?`,
      body: 'They\'ll be hidden from the subcontractor register. Existing induction records stay on file.',
      confirmLabel: 'Archive',
      danger: true,
    }))) return;
    try {
      await archiveSubcontractor(s.id);
      setShowModal(false);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <AppShell title="Subcontractors" back="/registers">
      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
          Auto-populated from subbie inductions. Tap a row to update insurance dates.
        </p>
      </div>

      
      <button type="button" className="btn btn-ghost" onClick={() => setBulkOpen(true)} style={{ marginBottom: 12 }}>
        Bulk import (CSV) →
      </button>
      {error && <div className="banner banner-error">{error}</div>}

      {loading ? (
        <LoadingIndicator />
      ) : items.length === 0 ? (
        <div className="card" style={{ textAlign: 'center' }}>
          <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
            No subcontractors yet. Run a subbie induction from a site's Forms menu.
          </p>
        </div>
      ) : (
        <>
          <p className="muted" style={{ margin: '0 0 8px', fontSize: 12 }}>
            Tap the <strong>🩹 FA</strong> badge on any row to mark / unmark them as a first aider. Tap the row name to edit the rest.
          </p>
          <ul className="person-list">
            {items.map((s) => (
              <li key={s.id}>
                <div className="site-item-link" style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                  <button
                    type="button"
                    onClick={() => startEdit(s)}
                    style={{ flex: 1, textAlign: 'left', font: 'inherit', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                  >
                    <p className="site-name">{s.company_name}</p>
                    <p className="site-meta">
                      {s.trade ?? 'Unknown trade'} · PL exp {fmtDate(s.pl_insurance_expiry)} · EL exp {fmtDate(s.el_insurance_expiry)}
                    </p>
                  </button>
                  <button
                    type="button"
                    className={`status-pill ${s.first_aider ? 'status-ok' : 'status-muted'}`}
                    onClick={(e) => { e.stopPropagation(); toggleFirstAider(s); }}
                    aria-pressed={s.first_aider}
                    aria-label={s.first_aider
                      ? `Unmark ${s.company_name} as first aider`
                      : `Mark ${s.company_name} as first aider`}
                    title={s.first_aider
                      ? 'First aider — tap to unmark'
                      : 'Not a first aider — tap to mark'}
                    style={{ cursor: 'pointer', flexShrink: 0, fontSize: 11, padding: '4px 10px' }}
                  >
                    🩹 FA
                  </button>
                  <span className="chev">›</span>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      <Modal open={showModal && !!draft} title={editing?.company_name ?? 'Edit'} onClose={() => { setShowModal(false); setEditing(null); }}>
        {draft && (
          <form onSubmit={onSave}>
            <div className="field"><label htmlFor="sub-company">Company name</label><input id="sub-company" type="text" required value={draft.company_name} onChange={(e) => setDraft({ ...draft, company_name: e.target.value })} /></div>
            <div className="field"><label htmlFor="sub-trade">Trade</label><input id="sub-trade" type="text" value={draft.trade} onChange={(e) => setDraft({ ...draft, trade: e.target.value })} /></div>
            <div className="field-row">
              <div className="field"><label htmlFor="sub-contact">Main contact</label><input id="sub-contact" type="text" value={draft.main_contact} onChange={(e) => setDraft({ ...draft, main_contact: e.target.value })} /></div>
              <div className="field"><label htmlFor="sub-mobile">Mobile</label><input id="sub-mobile" type="tel" value={draft.mobile} onChange={(e) => setDraft({ ...draft, mobile: e.target.value })} /></div>
            </div>
            <AddressInput
              label="Address"
              value={draft.address}
              onChange={(v) => setDraft({ ...draft, address: v })}
            />
            <div className="field-row">
              <div className="field"><label htmlFor="sub-pl-exp">Public Liability expiry</label><input id="sub-pl-exp" type="date" value={draft.pl_insurance_expiry} onChange={(e) => setDraft({ ...draft, pl_insurance_expiry: e.target.value })} /></div>
              <div className="field"><label htmlFor="sub-el-exp">Employers Liability expiry</label><input id="sub-el-exp" type="date" value={draft.el_insurance_expiry} onChange={(e) => setDraft({ ...draft, el_insurance_expiry: e.target.value })} /></div>
            </div>
            <div className="field"><label htmlFor="sub-pi-exp">Professional Indemnity expiry</label><input id="sub-pi-exp" type="date" value={draft.pi_insurance_expiry} onChange={(e) => setDraft({ ...draft, pi_insurance_expiry: e.target.value })} /></div>

            <h3 className="section-title" style={{ marginTop: 16 }}>First aid</h3>
            <div className="field">
              <label className="check-row" style={{ alignItems: 'flex-start' }}>
                <input
                  type="checkbox"
                  checked={draft.first_aider}
                  onChange={(e) => setDraft({ ...draft, first_aider: e.target.checked })}
                />
                <span>
                  <strong>Qualified first aider</strong>
                  <span className="muted" style={{ display: 'block', fontSize: 12, marginTop: 2 }}>
                    Holds a current FAW / EFAW / equivalent certificate.
                  </span>
                </span>
              </label>
            </div>
            {draft.first_aider && (
              <div className="field">
                <label htmlFor="sub-fa-exp">First aid certificate expiry</label>
                <input
                  id="sub-fa-exp"
                  type="date"
                  value={draft.first_aid_cert_expiry}
                  onChange={(e) => setDraft({ ...draft, first_aid_cert_expiry: e.target.value })}
                />
                <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
                  FAW certs run 3 years from issue. Leave blank if you don&apos;t have it.
                </p>
              </div>
            )}

            <div className="btn-stack">
              <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Saving…' : 'Save'}</button>
              {editing && <button type="button" className="btn btn-ghost" style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={() => onArchive(editing)}>Archive</button>}
              <button type="button" className="btn btn-ghost" onClick={() => { setShowModal(false); setEditing(null); }}>Cancel</button>
            </div>
          </form>
        )}
      </Modal>
    <BulkImportModal
        open={bulkOpen}
        title="Bulk import sub-contractors"
        columns={[
          { key: 'company_name',        label: 'Company',     required: true,  hint: 'Trading name' },
          { key: 'trade',               label: 'Trade',                          hint: 'e.g. Roofing, Electrical' },
          { key: 'main_contact',        label: 'Contact',                        hint: 'Foreman / director' },
          { key: 'mobile',              label: 'Mobile',                         hint: 'Optional' },
          { key: 'pl_insurance_expiry', label: 'Public Liability expires',       hint: 'YYYY-MM-DD.' },
          { key: 'el_insurance_expiry', label: 'Employers Liability expires',    hint: 'YYYY-MM-DD.' },
        ]}
        exampleRows={[
          'company_name,trade,main_contact,mobile,pl_expires,el_expires',
          'ACME Bricklaying Ltd,Bricklaying,Mike Doyle,07700 900100,2026-09-30,2026-09-30',
          'ACME Roofing Ltd,Roofing,Pete Hill,07700 900200,2026-08-15,',
        ]}
        toInput={(row) => row.company_name?.trim() ? ({
          orgId: org!.org_id,
          company_name: row.company_name.trim(),
          trade: row.trade?.trim() || null,
          main_contact: row.main_contact?.trim() || null,
          mobile: row.mobile?.trim() || null,
          address: null,
          pl_insurance_expiry: row.pl_insurance_expiry?.trim() || null,
          el_insurance_expiry: row.el_insurance_expiry?.trim() || null,
          approved_by: null,
        }) : null}
        createRow={async (input) => { await findOrCreateSubcontractor(input); }}
        onClose={() => setBulkOpen(false)}
        onImported={async () => { setBulkOpen(false); await refresh(); }}
      />
    </AppShell>
  );
}
